import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faTimes, faEdit } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../AxiosInstance';
import { LogoInput } from '../../components/input/inputs';
import styles from './scada.module.css';

function StateRow({ state, onChange, onDelete }) {
    const { t } = useTranslation();
    return (
        <div className={styles.tableRow} style={{ gridTemplateColumns: '60px 1fr 36px 28px' }}>
            <input
                type="number"
                value={state.value}
                onChange={e => onChange({ ...state, value: e.target.value })}
                placeholder={t('scada.stateValue')}
                className={styles.inspectorInput}
                style={{ marginBottom: 0 }}
            />
            <input
                type="text"
                value={state.label}
                onChange={e => onChange({ ...state, label: e.target.value })}
                placeholder={t('scada.stateLabel')}
                className={styles.inspectorInput}
                style={{ marginBottom: 0 }}
            />
            <input
                type="color"
                value={state.color || '#22C55E'}
                onChange={e => onChange({ ...state, color: e.target.value })}
                className={styles.colorSwatch}
            />
            <button className={styles.removeBtn} onClick={onDelete}><FontAwesomeIcon icon={faTrash} /></button>
        </div>
    );
}

function ActionRow({ action, onChange, onDelete }) {
    const { t } = useTranslation();
    return (
        <div className={styles.tableRow} style={{ gridTemplateColumns: '1fr 70px auto 28px' }}>
            <input
                type="text"
                value={action.label}
                onChange={e => onChange({ ...action, label: e.target.value })}
                placeholder={t('scada.actionLabel')}
                className={styles.inspectorInput}
                style={{ marginBottom: 0 }}
            />
            <input
                type="text"
                value={action.publish_value}
                onChange={e => onChange({ ...action, publish_value: e.target.value })}
                placeholder={t('scada.actionValue')}
                className={styles.inspectorInput}
                style={{ marginBottom: 0 }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#94A3B8', whiteSpace: 'nowrap' }}>
                <input
                    type="checkbox"
                    checked={!!action.confirm_required}
                    onChange={e => onChange({ ...action, confirm_required: e.target.checked })}
                />
                {t('scada.confirmAction')}
            </label>
            <button className={styles.removeBtn} onClick={onDelete}><FontAwesomeIcon icon={faTrash} /></button>
        </div>
    );
}

function MachineTypeForm({ projectId, initial, onSave, onCancel }) {
    const { t } = useTranslation();
    const [name, setName] = useState(initial?.name || '');
    const [description, setDescription] = useState(initial?.description || '');
    const [states, setStates] = useState(initial?.states_config || []);
    const [actions, setActions] = useState(initial?.actions_config || []);
    const [imageForm, setImageForm] = useState({});
    const [saving, setSaving] = useState(false);

    const addState = () => setStates(prev => [...prev, { value: prev.length, label: t('scada.defaultStateName'), color: '#22C55E' }]);
    const addAction = () => setActions(prev => [...prev, { label: t('scada.defaultActionName'), publish_value: '1', confirm_required: false }]);

    const save = async () => {
        setSaving(true);
        const formData = new FormData();
        formData.append('name', name);
        formData.append('description', description);
        formData.append('project', projectId);
        formData.append('states_config', JSON.stringify(states));
        formData.append('actions_config', JSON.stringify(actions));
        if (imageForm.image instanceof File) {
            formData.append('image', imageForm.image);
        }
        try {
            if (initial?.id) {
                await axiosInstance.put(`scada_machine_type/${initial.id}/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            } else {
                await axiosInstance.post('scada_machine_type/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            onSave();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: '16px' }}>
            <div className={styles.inspectorLabel}>{t('scada.machineTypeName')}</div>
            <input value={name} onChange={e => setName(e.target.value)} className={styles.inspectorInput} placeholder={t('scada.machineTypeName')} />

            <div className={styles.inspectorLabel} style={{ marginTop: 8 }}>{t('scada.machineImage')}</div>
            <LogoInput name="image" internalName="image" form={imageForm} setForm={setImageForm} current={initial?.image_url} />

            <div className={styles.inspectorLabel} style={{ marginTop: 12 }}>{t('scada.states')}</div>
            {states.map((s, i) => (
                <StateRow key={i} state={s}
                    onChange={ns => setStates(prev => prev.map((x, j) => j === i ? ns : x))}
                    onDelete={() => setStates(prev => prev.filter((_, j) => j !== i))}
                />
            ))}
            <button className={styles.addRowBtn} onClick={addState}><FontAwesomeIcon icon={faPlus} className="me-1" />{t('scada.addState')}</button>

            <div className={styles.inspectorLabel} style={{ marginTop: 12 }}>{t('scada.actions')}</div>
            {actions.map((a, i) => (
                <ActionRow key={i} action={a}
                    onChange={na => setActions(prev => prev.map((x, j) => j === i ? na : x))}
                    onDelete={() => setActions(prev => prev.filter((_, j) => j !== i))}
                />
            ))}
            <button className={styles.addRowBtn} onClick={addAction}><FontAwesomeIcon icon={faPlus} className="me-1" />{t('scada.addAction')}</button>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button className={styles.primaryBtn} onClick={save} disabled={saving}>
                    {saving ? '...' : t('common.save')}
                </button>
                <button className={styles.secondaryBtn} onClick={onCancel}>{t('common.cancel') || 'Cancel'}</button>
            </div>
        </div>
    );
}

export default function SCADAMachineTypes({ projectId, onClose }) {
    const { t } = useTranslation();
    const [types, setTypes] = useState([]);
    const [editing, setEditing] = useState(null); // null = list, {} = new, {id,...} = edit

    const load = () => {
        axiosInstance.get(`scada_machine_type/?project_id=${projectId}`).then(res => {
            setTypes(res.data.results || res.data);
        });
    };

    useEffect(() => { load(); }, [projectId]);

    const remove = (id) => {
        if (!window.confirm(t('scada.deleteMachineTypeConfirm'))) return;
        axiosInstance.delete(`scada_machine_type/${id}/`).then(load);
    };

    if (editing !== null) {
        return (
            <div style={{ background: '#0F172A', minHeight: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #334155' }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#F1F5F9' }}>
                        {editing?.id ? t('scada.editMachineType') : t('scada.addMachineType')}
                    </div>
                    <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <MachineTypeForm
                    projectId={projectId}
                    initial={editing?.id ? editing : null}
                    onSave={() => { load(); setEditing(null); }}
                    onCancel={() => setEditing(null)}
                />
            </div>
        );
    }

    return (
        <div style={{ background: '#0F172A', minHeight: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #334155' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#F1F5F9' }}>{t('scada.myMachines')}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className={styles.primaryBtn} style={{ fontSize: 11, padding: '5px 12px' }} onClick={() => setEditing({})}>
                        <FontAwesomeIcon icon={faPlus} />{t('scada.addMachineType')}
                    </button>
                    {onClose && (
                        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, padding: 16 }}>
                {types.map(mt => (
                    <div key={mt.id} className={styles.machineCard}>
                        <div className={styles.machineCardImg}>
                            {mt.image_url
                                ? <img src={mt.image_url} alt={mt.name} />
                                : <div style={{ color: '#475569', fontSize: 11 }}>{t('scada.noMachineImage')}</div>
                            }
                        </div>
                        <div className={styles.machineCardBody}>
                            <div className={styles.machineCardName}>{mt.name}</div>
                            <div className={styles.machineCardMeta}>
                                {mt.states_config?.length || 0} {t('scada.states').toLowerCase()} · {mt.instance_count || 0} inst.
                            </div>
                            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                <button className={styles.secondaryBtn} style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => setEditing(mt)}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button className={styles.secondaryBtn} style={{ fontSize: 10, padding: '3px 8px', color: '#EF4444', borderColor: 'transparent' }} onClick={() => remove(mt.id)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {types.length === 0 && (
                    <div style={{ gridColumn: '1/-1', color: '#475569', textAlign: 'center', padding: 24, fontSize: 13 }}>
                        {t('scada.noScreens')}
                    </div>
                )}
            </div>
        </div>
    );
}
